import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { CreateView } from "./CreateView";
import { ManageView } from "./ManageView";

import { CreatedView } from "@/components/PromptTemplate/OverviewContent/SharePromptButton/CreatedView";
import {
  ShareDialogMode,
  ShareSessionDialogShell,
  defaultPinnedVersionSelection,
  pinnedVersionSelectionToCreateBodyFields,
  useShareSessionDialog,
  type PinnedVersionSelection,
} from "@/components/SharedSessions";
import { useShareSkillCollection } from "@/queries/prompts";
import type { SharedSession } from "@/queries/prompts/types";
import type { UpdateSharedSessionRequest } from "@/queries/prompts/useUpdateSharedSession";

interface ShareSkillCollectionButtonProps {
  collectionName: string;
  selectedVersionNumber: number | null;
  latestVersionNumber: number | undefined;
}

export const ShareSkillCollectionButton = ({
  collectionName,
  selectedVersionNumber,
  latestVersionNumber,
}: ShareSkillCollectionButtonProps) => {
  const { workspaceId, collectionId } = useParams();
  const shareSkillCollectionMutation = useShareSkillCollection();
  const shareTarget = useMemo(() => {
    const parsedWorkspaceId = workspaceId ? Number.parseInt(workspaceId, 10) : Number.NaN;

    if (Number.isNaN(parsedWorkspaceId) || !collectionId) {
      return null;
    }

    return {
      workspaceId: parsedWorkspaceId,
      collectionId,
    };
  }, [collectionId, workspaceId]);

  const defaultVersionSelection = useMemo(
    () => defaultPinnedVersionSelection(selectedVersionNumber, latestVersionNumber),
    [latestVersionNumber, selectedVersionNumber],
  );

  const [remixEnabled, setRemixEnabled] = useState(true);

  const resetExtraFormState = useCallback(() => {
    setRemixEnabled(true);
  }, []);

  const buildCreateVariables = useCallback(
    (ctx: { name: string; is_public: boolean; versionSelection: PinnedVersionSelection }) => {
      if (!shareTarget) {
        return null;
      }

      return {
        workspace_id: shareTarget.workspaceId,
        name: ctx.name,
        skill_collection_id: shareTarget.collectionId,
        is_public: ctx.is_public,
        remix_enabled: remixEnabled,
        ...pinnedVersionSelectionToCreateBodyFields(ctx.versionSelection),
      };
    },
    [remixEnabled, shareTarget],
  );

  const getDeleteVariables = useCallback(
    (sessionId: string) => {
      if (!shareTarget) {
        return null;
      }

      return {
        workspace_id: shareTarget.workspaceId,
        shared_session_id: sessionId,
        skill_collection_id: shareTarget.collectionId,
      };
    },
    [shareTarget],
  );

  const getUpdateVariables = useCallback(
    (
      sessionId: string,
      fields: Partial<
        Pick<
          SharedSession,
          | "name"
          | "is_public"
          | "batch_enabled"
          | "chat_enabled"
          | "remix_enabled"
          | "label_name"
          | "version_number"
        >
      >,
    ): UpdateSharedSessionRequest | null => {
      if (!shareTarget) {
        return null;
      }

      return {
        workspace_id: shareTarget.workspaceId,
        skill_collection_id: shareTarget.collectionId,
        shared_session_id: sessionId,
        ...fields,
      };
    },
    [shareTarget],
  );

  const {
    open,
    mode,
    setMode,
    userChoseCreate,
    name,
    setName,
    isPublic,
    setIsPublic,
    versionSelection,
    setVersionSelection,
    newShareUrl,
    hasExistingSessions,
    isCheckingExistence,
    sessions,
    isLoadingSessions,
    resetForm,
    handleOpenChange,
    handleCreate,
    handleDeleteSession,
    handleUpdateSession,
    handleNavigateToManage,
    isCreateDisabled,
    hasSessionsToManage,
  } = useShareSessionDialog({
    queryTarget: {
      workspaceId: shareTarget?.workspaceId,
      skillCollectionId: shareTarget?.collectionId,
      promptId: undefined,
      workflowId: undefined,
    },
    existsQueryEnabled: !!shareTarget,
    resourceName: collectionName,
    defaultVersionSelection,
    createMutation: shareSkillCollectionMutation,
    resetCreateMutation: () => shareSkillCollectionMutation.reset(),
    buildCreateVariables,
    getDeleteVariables,
    getUpdateVariables,
    resetExtraFormState,
  });

  const handleNewShareLink = useCallback(() => {
    userChoseCreate.current = true;
    resetForm();
    setMode(ShareDialogMode.CREATE);
  }, [resetForm, setMode, userChoseCreate]);

  if (!shareTarget) {
    return null;
  }

  return (
    <ShareSessionDialogShell
      open={open}
      onOpenChange={handleOpenChange}
      hasExistingSessions={hasExistingSessions}
      isCheckingExistence={isCheckingExistence}
      sessions={sessions}
      triggerSize="xs"
      triggerAriaLabel="Share skills"
    >
      {mode === ShareDialogMode.CREATED && newShareUrl && (
        <CreatedView
          key="created"
          shareUrl={newShareUrl}
          isPublic={isPublic}
          hasExistingSessions={hasSessionsToManage}
          onManage={handleNavigateToManage}
        />
      )}

      {mode === ShareDialogMode.MANAGE && (
        <ManageView
          key="manage"
          workspaceId={shareTarget.workspaceId}
          collectionId={shareTarget.collectionId}
          sessions={sessions}
          isLoadingSessions={isLoadingSessions}
          onUpdate={handleUpdateSession}
          onDelete={handleDeleteSession}
          onNewLink={handleNewShareLink}
        />
      )}

      {mode === ShareDialogMode.CREATE && (
        <CreateView
          key="create"
          workspaceId={shareTarget.workspaceId}
          collectionId={shareTarget.collectionId}
          name={name}
          onNameChange={setName}
          isPublic={isPublic}
          onIsPublicChange={setIsPublic}
          versionSelection={versionSelection}
          onVersionSelectionChange={setVersionSelection}
          remixEnabled={remixEnabled}
          onRemixEnabledChange={setRemixEnabled}
          hasExistingSessions={hasSessionsToManage}
          isCreateDisabled={isCreateDisabled}
          isPending={shareSkillCollectionMutation.isPending}
          onCreate={handleCreate}
          onBack={handleNavigateToManage}
        />
      )}
    </ShareSessionDialogShell>
  );
};
